import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, FileSpreadsheet, Maximize2, RotateCcw } from "lucide-react";
import { PART_A } from "./gtm/slidesA";
import { PART_B } from "./gtm/slidesB";
import "./pitch/deck.css";
import "./gtm/gtm.css";

const SLIDES = [...PART_A, ...PART_B];
const TOTAL = SLIDES.length;
const SECTIONS = [
  ["Strategy", 2], ["Channels", 12], ["Pricing", 13], ["Plan", 14], ["Funnel", 21],
  ["P&L", 23], ["Unit econ", 24], ["Milestones", 26], ["Team", 28], ["Cash", 30],
];
const BY_START = [...SECTIONS].sort((a, b) => a[1] - b[1]);

export default function GTMStrategy() {
  const [i, setI] = useState(() => {
    const h = parseInt((window.location.hash || "").replace("#", ""), 10);
    return Number.isFinite(h) && h >= 1 && h <= TOTAL ? h - 1 : 0;
  });
  const [scale, setScale] = useState(1);
  const [portrait, setPortrait] = useState(false);
  const touch = useRef(null);

  useLayoutEffect(() => {
    const fit = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const reserve = vh < 620 ? 110 : 56;
      setScale(Math.min(vw / 960, (vh - reserve) / 540));
      setPortrait(vh > vw && vw < 820);
    };
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, []);

  const go = useCallback((n) => {
    const next = Math.max(0, Math.min(TOTAL - 1, n));
    setI(next);
    window.history.replaceState(null, "", `#${next + 1}`);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); go(i + 1); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(i - 1); }
      else if (e.key === "Home") go(0);
      else if (e.key === "End") go(TOTAL - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, go]);

  useEffect(() => {
    const onHash = () => {
      const h = parseInt((window.location.hash || "").replace("#", ""), 10);
      if (Number.isFinite(h) && h >= 1 && h <= TOTAL) setI(h - 1);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? i + 1 : i - 1);
    touch.current = null;
  };

  const fullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const Slide = SLIDES[i];
  const activeSection = BY_START.reduce((best, s) => (i + 1 >= s[1] ? s : best), BY_START[0]);

  return (
    <div className="vpd-root" data-testid="gtm-deck-root" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="vpd-progress" style={{ width: `${((i + 1) / TOTAL) * 100}%` }} data-testid="gtm-progress" />

      <div className="gtm-menu" data-testid="gtm-section-menu">
        {SECTIONS.map(([n, s]) => (
          <button
            key={n}
            ref={(el) => { if (el && activeSection[0] === n) el.scrollIntoView({ block: "nearest", inline: "nearest" }); }}
            className={`gtm-menu-item ${activeSection[0] === n ? "on" : ""}`}
            onClick={() => go(s - 1)}
            data-testid={`gtm-menu-${n.toLowerCase().replace(/[^a-z]/g, "")}`}
          >
            {n}
          </button>
        ))}
      </div>

      <div className="vpd-viewport">
        <div className="vpd-stage" style={{ transform: `scale(${scale})` }} data-testid="gtm-stage">
          <Slide key={i} />
        </div>
      </div>

      <div className="vpd-ui vpd-topbar">
        <a className="vpd-btn" href="/downloads/verqo-gtm-strategy.pdf" download data-testid="gtm-download-pdf">
          <Download size={13} /> PDF
        </a>
        <a className="vpd-btn" href="/downloads/verqo-gtm-model-24mo.xlsx" download data-testid="gtm-download-model">
          <FileSpreadsheet size={13} /> Model
        </a>
        <button className="vpd-btn" onClick={fullscreen} data-testid="gtm-fullscreen" aria-label="Full screen">
          <Maximize2 size={13} /> Full
        </button>
      </div>

      <div className="vpd-ui vpd-nav">
        <button className="vpd-arrow" onClick={() => go(i - 1)} disabled={i === 0} data-testid="gtm-prev" aria-label="Previous slide">
          <ChevronLeft size={17} />
        </button>
        <div className="vpd-dots" data-testid="gtm-dots">
          {SLIDES.map((_, n) => (
            <button key={n} className={`vpd-dot ${n === i ? "on" : ""}`} onClick={() => go(n)} data-testid={`gtm-dot-${n + 1}`} aria-label={`Slide ${n + 1}`} />
          ))}
        </div>
        <span className="vpd-navcount" data-testid="gtm-counter">
          <b>{String(i + 1).padStart(2, "0")}</b> / {TOTAL}
        </span>
        <button className="vpd-arrow" onClick={() => go(i + 1)} disabled={i === TOTAL - 1} data-testid="gtm-next" aria-label="Next slide">
          <ChevronRight size={17} />
        </button>
      </div>

      {portrait && (
        <div className="vpd-rotate" data-testid="gtm-rotate-hint">
          <RotateCcw size={12} /> ROTATE FOR FULL SIZE · SWIPE TO MOVE
        </div>
      )}
    </div>
  );
}
